import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut, Calendar, Clock, User, ArrowLeft, Check } from "lucide-react";
import api from "@/lib/api";
import { useClientAuth } from "@/context/ClientAuthContext";
import { useBooking } from "@/context/BookingContext";

function statusStyle(s) {
  return s === "confirmed" ? "text-green-600 bg-green-50 border-green-200"
    : s === "cancelled" ? "text-red-500 bg-red-50 border-red-200"
    : s === "no_show" ? "text-orange-500 bg-orange-50 border-orange-200"
    : "text-[#C5B382] bg-[#C5B382]/10 border-[#C5B382]/30";
}

function BookingCard({ b, upcoming, onRebook }) {
  return (
    <div data-testid={`account-booking-${b.id}`} className="border border-[#1C1917]/10 bg-white rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-serif text-xl">{b.service_name}</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#1C1917]/60 mt-2">
            <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#C5B382]" /> {b.date_display}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#C5B382]" /> {b.time}</span>
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider border px-2.5 py-1 rounded-full ${statusStyle(b.status)}`}>
          {b.status.replace("_", " ")}
        </span>
      </div>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#1C1917]/8">
        <div className="text-sm text-[#1C1917]/70">
          ${Number(b.amount_paid).toFixed(2)} paid
          {b.remaining > 0 && <span className="text-[#1C1917]/45"> · ${Number(b.remaining).toFixed(2)} due at appointment</span>}
        </div>
        {upcoming && b.status !== "cancelled" && (
          <Link data-testid={`account-manage-${b.id}`} to={`/manage/${b.manage_token}`}
            className="text-xs font-semibold tracking-wider uppercase text-[#C5B382] hover:underline">
            Reschedule / Cancel
          </Link>
        )}
        {!upcoming && (
          <button data-testid={`account-rebook-${b.id}`} onClick={() => onRebook(b.service_id)}
            className="text-xs font-semibold tracking-[0.15em] uppercase text-[#1C1917] bg-[#C5B382] hover:bg-[#B8A572] px-4 py-2 rounded-full transition-colors">
            Book again
          </button>
        )}
      </div>
    </div>
  );
}

export default function AccountPage() {
  const { client, logout } = useClientAuth();
  const { openBooking } = useBooking();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [saved, setSaved] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (client === false) { navigate("/"); }
    if (client) {
      setProfile({ name: client.name || "", phone: client.phone || "" });
      api.get("/client/bookings").then((r) => setData(r.data)).catch(() => setData({ upcoming: [], past: [] }));
    }
  }, [client, navigate]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/client/profile", profile);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSavingProfile(false); }
  };

  const handleRebook = (serviceId) => { openBooking(serviceId); navigate("/"); };

  if (client === null || client === false) {
    return <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center"><Loader2 className="animate-spin text-[#C5B382]" /></div>;
  }

  return (
    <div data-testid="account-page" className="min-h-screen bg-[#FAF8F5] text-[#1C1917]">
      <header className="border-b border-[#1C1917]/10 bg-white/70 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="account-home" className="flex items-center gap-2 text-sm text-[#1C1917]/60 hover:text-[#C5B382] transition-colors">
            <ArrowLeft size={16} /> Back to site
          </Link>
          <button data-testid="account-logout" onClick={async () => { await logout(); navigate("/"); }}
            className="flex items-center gap-2 text-sm text-[#1C1917]/60 hover:text-[#C5B382] transition-colors">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 space-y-10">
        <div>
          <div className="eyebrow text-[#C5B382] mb-2">My Account</div>
          <h1 className="font-serif text-4xl sm:text-5xl">Hello, {client.name?.split(" ")[0] || "there"}</h1>
        </div>

        {/* Profile */}
        <section className="bg-white border border-[#1C1917]/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><User size={16} className="text-[#C5B382]" /><span className="eyebrow text-[#1C1917]/50">Your details</span></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow text-[#1C1917]/45 block mb-2">Full name</label>
              <input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="w-full bg-[#FAF8F5] border border-[#1C1917]/15 rounded-xl focus:border-[#C5B382] outline-none px-4 py-3 text-sm" />
            </div>
            <div>
              <label className="eyebrow text-[#1C1917]/45 block mb-2">Phone number</label>
              <input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                className="w-full bg-[#FAF8F5] border border-[#1C1917]/15 rounded-xl focus:border-[#C5B382] outline-none px-4 py-3 text-sm" />
            </div>
          </div>
          <div className="text-xs text-[#1C1917]/45 mt-3">{client.email}</div>
          <button data-testid="profile-save" onClick={saveProfile} disabled={savingProfile}
            className="mt-5 inline-flex items-center gap-2 bg-[#C5B382] hover:bg-[#B8A572] text-[#1C1917] text-[11px] font-semibold tracking-[0.15em] uppercase px-6 py-3 rounded-full transition-colors">
            {saved ? <><Check size={14} /> Saved</> : savingProfile ? "Saving…" : "Save details"}
          </button>
        </section>

        {/* Bookings */}
        {!data ? (
          <Loader2 className="animate-spin text-[#C5B382]" />
        ) : (
          <>
            <section>
              <h2 className="font-serif text-2xl mb-4">Upcoming appointments</h2>
              {data.upcoming.length ? (
                <div className="space-y-3" data-testid="account-upcoming">{data.upcoming.map((b) => <BookingCard key={b.id} b={b} upcoming />)}</div>
              ) : (
                <div data-testid="account-no-upcoming" className="bg-white border border-dashed border-[#1C1917]/15 rounded-2xl p-8 text-center">
                  <p className="text-[#1C1917]/55">No upcoming appointments.</p>
                  <button data-testid="account-book" onClick={() => { openBooking(); navigate("/"); }} className="mt-4 bg-[#C5B382] hover:bg-[#B8A572] text-[#1C1917] text-[11px] font-semibold tracking-[0.15em] uppercase px-7 py-3 rounded-full transition-colors">Book an appointment</button>
                </div>
              )}
            </section>

            {data.past.length > 0 && (
              <section>
                <h2 className="font-serif text-2xl mb-4">Past appointments</h2>
                <div className="space-y-3" data-testid="account-past">{data.past.map((b) => <BookingCard key={b.id} b={b} upcoming={false} onRebook={handleRebook} />)}</div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
